import { Spin, Card, Button, Input, Modal, Alert, Form } from 'antd';
import { useEffect, useState } from 'react';
import { useStoreActions, useStoreState } from '../../store';
import { downloadCsvFile, validateCsvHasDataRows, validateCsvHeaders } from '../../store/files/fileUtils';
import { PresignedUpload, UploadType } from '../shared/PresignedUpload';
import { CloudUploadOutlined, CloudDownloadOutlined } from '@ant-design/icons';
import type { TntJobV2, TntType, TntUsageType } from '../../store/services/TimeInTransitService/types';
import { tntUsageTypeToApi } from '../../store/services/TimeInTransitService/types';
import parseStatusCode from '../../store/errors/parseStatusCode';
import { TNT_UPLOAD_TYPE_CONFIG, buildTemplateCsv } from './tntUploadTypeConfig';
import { resolveJobError, resolveUploadError } from './tntUploadError';
import type { UploadErrorDetail } from './tntUploadError';
import { pollUploadJob } from './pollUploadJob';
import { findDataSetId } from './findDataSetId';
import type { DataSetEntries } from './findDataSetId';
import type { UploadJobPollResult } from './pollUploadJob';
import type { UploadProdTntResponse } from '../../store/services/TimeInTransitService/types';
import type { RcFile } from 'antd/lib/upload';
import type { TntUploadTypeConfig } from './tntUploadTypeConfig';

export interface TntUploadProps {
  type: TntType;
  carrierId?: string;
}

const tntUploadTypes: Record<TntUsageType, string> = {
  SIMULATION: 'Simulation',
  PROD: 'Production',
};

// Kept as a named re-export: callers and tests already import it from here.
export const findExistingDataSetId = (timeInTransitState: DataSetEntries, tntSourceName: string): string | null =>
  findDataSetId(timeInTransitState, tntSourceName, 'PROD');

// disableUpload tracks an in-progress upload for *this* type's own dataset (typeConfig.tntSourceName).
// Scoped to production and promise only: simulation and carrier_default share tntSourceName ===
// 'partner-provided-postal' / 'carrier-default' with other usages (or, for carrier_default, with every
// carrier's dataset via a single unscoped lookup), so gating their button on disableUpload too would
// disable it based on an unrelated dataset's job status — a real behavior change, not just a widening
// of the production-only gate this replaces.
export const shouldDisableStartUpload = (type: TntType, disableUpload: boolean): boolean =>
  (type === 'production' || type === 'promise') && disableUpload;

// Promise only. A header-only promise file uploads fine and then activates nothing, and the
// activation failure is not visible anywhere the uploader can see it yet, so this is the only
// warning they get. The carrier-selection tabs are deliberately left alone: a header-only file
// combined with fullReplace is a plausible way to clear a dataset there, and rejecting it would be
// a behavior change to a shipping feature rather than a guard on a new one.
export const buildTntUploadValidator =
  (type: TntType, typeConfig: TntUploadTypeConfig) =>
  async (file: RcFile): Promise<void> => {
    // Headers first: a file with the wrong columns should report that, not "no data rows".
    await validateCsvHeaders(typeConfig.requiredHeaders, typeConfig.requiredHeaders)(file);
    if (type === 'promise') {
      await validateCsvHasDataRows(file);
    }
  };

// Renders whatever detail the rejection carried. resolveUploadError always yields something,
// so this can never produce an empty modal body.
const renderErrorDetail = (detail: UploadErrorDetail) => {
  if (detail.kind === 'rows') {
    return (
      <>
        {detail.rows.map((row, i) => (
          <p key={`row-${row.rowNumber}-${i}`}>
            Row {row.rowNumber}:{' '}
            {(row.invalidColumnMessages ?? []).map((msg, j) => (
              <span key={`msg-${j}`}>
                {msg}
                <br />
              </span>
            ))}
          </p>
        ))}
      </>
    );
  }

  if (detail.kind === 'rollups') {
    return (
      <>
        {detail.rollups.map((rollup, i) => (
          <p key={`rollup-${i}`}>
            {rollup.errorDetail ?? 'Invalid records'}
            {rollup.errorLocation ? ` (${rollup.errorLocation})` : ''}
            {typeof rollup.count === 'number' ? ` — ${rollup.count} row(s)` : ''}
          </p>
        ))}
      </>
    );
  }

  return <p>{detail.message}</p>;
};

// Synchronous rejection (axios error returned by the thunk).
export const renderUploadError = (error: unknown) => renderErrorDetail(resolveUploadError(error));

// Asynchronous failure (job reached uploadJobStatus 'FAILED').
export const renderJobError = (job: unknown) => renderErrorDetail(resolveJobError(job));

// The name/description are cleared once the submission is done with, but NOT on failure —
// a rejected file is usually corrected and resubmitted under the same name, and clearing it
// would make the user retype it. TIMED_OUT counts as done: the file was accepted.
// Which job-fetch thunk owns each tab's store slot. The promise tab deliberately has its own
// `promiseJobs` slot, so refetching with the production thunk here would clobber it.
export const jobRefetchThunkFor = (type: TntType): 'promise' | 'production' | 'simulation' | 'carrierDefault' => {
  if (type === 'promise') return 'promise';
  if (type === 'simulation') return 'simulation';
  if (type === 'carrier_default') return 'carrierDefault';
  return 'production';
};

// A job only leaves the table stale while it is still running; both terminal outcomes change
// what the table should show — COMPLETE adds the new version, FAILED adds a row whose error
// file the user needs to reach.
export const shouldRefetchJobsAfter = (status: UploadJobPollResult['status']): boolean =>
  status === 'COMPLETE' || status === 'FAILED';

export const shouldResetFormAfter = (status: UploadJobPollResult['status']): boolean =>
  status === 'COMPLETE' || status === 'TIMED_OUT';

export const TntUpload = ({ type, carrierId }: TntUploadProps) => {
  const createAsyncDataSetTypeFromUploadProd = useStoreActions(
    (actions) => actions.timeInTransit.createAsyncDataSetTypeFromUploadV2,
  );
  const updateDataSetType = useStoreActions((actions) => actions.timeInTransit.updateDataSetTypeFromUploadV2);
  const fetchTntJobById = useStoreActions((actions) => actions.timeInTransit.fetchTntJobByIdV2);
  const fetchPromiseTntJobs = useStoreActions((actions) => actions.timeInTransit.fetchPromiseTntJobsV2);
  const fetchProductionTntJobs = useStoreActions((actions) => actions.timeInTransit.fetchTntJobsV2);
  const fetchSimulationTntJobs = useStoreActions((actions) => actions.timeInTransit.fetchSimulationTntJobsV2);
  const fetchCarrierDefaultTntJobs = useStoreActions((actions) => actions.timeInTransit.fetchCarrierDefaultTntJobsV2);
  const timeInTransitState = useStoreState((state) => state.timeInTransit.timeInTransitState);
  const tntCarrierDefaultTableDataWithName = useStoreState(
    (state) => state.timeInTransit.tntCarrierDefaultTableDataWithName,
  );
  const uploadType = tntUsageTypeToApi[type];
  const typeConfig = TNT_UPLOAD_TYPE_CONFIG[type];

  // The Upload Name input lives inside a <Form.Item name="type">, and antd takes control of a
  // named item's child — injecting its own value/onChange over anything passed in. Clearing
  // React state alone leaves the rendered text untouched, so the field is reset via the form.
  const [form] = Form.useForm();
  const [uploadName, setUploadName] = useState<string>('');
  const [uploadDescription, setUploadDescription] = useState<string>('');
  const uploadLoadingState = useState<boolean>(false);

  const [uploadLoading] = uploadLoadingState;

  const [modal, contextHolder] = Modal.useModal();

  const [canSubmit, setCanSubmit] = useState<boolean>(false);

  const [isError, setIsError] = useState<boolean>(false);

  // PresignedUpload does not await onSuccess, so it clears uploadLoading as soon as the file
  // reaches S3 — before the job has been polled. This covers that second phase.
  const [processing, setProcessing] = useState<boolean>(false);

  // Every failure exit goes through here. The name is deliberately left in place so a corrected
  // file can be resubmitted under it, and canSubmit must be cleared for that resubmission to be
  // possible at all: PresignedUpload uploads only on a false -> true transition of startUpload.
  const reportUploadFailure = (content: React.ReactNode) => {
    setCanSubmit(false);
    modal.error({ title: 'Upload failed', content });
  };

  const resetForm = () => {
    form.resetFields();
    setUploadName('');
    setUploadDescription('');
    setCanSubmit(false);
  };

  const handleStartUpload = async () => {
    if (uploadName && Object.values(timeInTransitState).filter((entry) => entry.name === uploadName).length === 0) {
      setCanSubmit(true);
      setIsError(false);
    } else {
      setCanSubmit(false);
      setIsError(true);
    }
  };

  const [disableUpload, setDisableUpload] = useState(false);

  useEffect(() => {
    setDisableUpload(
      Object.values(timeInTransitState)
        .filter((tnt) => tnt.usage === 'PROD' && tnt?.tntSource?.name === typeConfig.tntSourceName)?.[0]
        ?.tntJobs.filter((job) => job.uploadJobStatus === 'IN_PROGRESS').length > 0,
    );
  }, [timeInTransitState, typeConfig.tntSourceName]);

  return (
    <Spin spinning={uploadLoading || processing} tip={processing ? 'Processing upload…' : undefined}>
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '20px' }}>
            <div>Upload Time in Transit Overrides</div>
            <Button
              title="Download"
              onClick={() => downloadCsvFile(buildTemplateCsv(type), typeConfig.templateFileName)}
            >
              Download Template <CloudDownloadOutlined />
            </Button>
          </div>
        }
      >
        <div style={{ display: 'flex', gap: '20px', marginBottom: '10px' }}>
          <div>
            <Form form={form} layout="vertical" name="upload" scrollToFirstError={true}>
              <Form.Item name={'type'} required={true} style={{ verticalAlign: 'middle' }} label={<b>Upload Name</b>}>
                <Input
                  autoComplete="one-time-code" // disable chrome autofill
                  style={{ minWidth: '250px' }}
                  placeholder={`tnt-overrides-${new Date().getFullYear()}`}
                  onChange={(e) => setUploadName(e.target.value)}
                />
              </Form.Item>
            </Form>
          </div>
          <div>
            <h4>Upload Description</h4>
            <Input
              autoComplete="one-time-code" // disable chrome autofill
              style={{ minWidth: '250px' }}
              placeholder={`Time In Transit Overrides for ${new Date().getFullYear()}`}
              value={uploadDescription}
              onChange={(e) => setUploadDescription(e.target.value)}
            />
          </div>
          <Button
            onClick={handleStartUpload}
            disabled={processing || shouldDisableStartUpload(type, disableUpload)}
            type="primary"
            style={{ marginTop: 28.5 }}
          >
            Start Upload
          </Button>
        </div>

        {isError && (
          <Alert
            description="Time in Transit Overrides must have a unique name."
            type="error"
            showIcon
            style={{ marginBottom: '10px' }}
          />
        )}
        <PresignedUpload
          uploadType={UploadType.TNT}
          validate={buildTntUploadValidator(type, typeConfig)}
          dragger={true}
          showUpload={true}
          loadingState={uploadLoadingState}
          expiration={600}
          uploadControl={{ mode: 'manual', startUpload: canSubmit }}
          onSuccess={async (res) => {
            try {
              // ex: partnerId/shipium-tnt-management-partner-overrides-upload-stage/uuid-1234.csv ==> uuid-1234
              const uploadStrings = res.s3Key.split('/');
              const file = uploadStrings[uploadStrings.length - 1];
              const fileString = file.slice(0, file.lastIndexOf('.'));

              const fileName = uploadName.trim() || fileString;
              const fileDescription = uploadDescription.trim() || '';
              const existingDataSetId = findExistingDataSetId(timeInTransitState, typeConfig.tntSourceName);
              const carrierDefaultDataSetId =
                tntCarrierDefaultTableDataWithName.filter((data: TntJobV2) => data.carrierId === carrierId)?.[0]
                  ?.tntDataSetId || null;
              //const carrierDefaultExists = (carrierId: string) => tntCarrierDefaultTableDataWithName.filter((data: TntJobV2) => data.carrierId === carrierId).length > 0;
              const uploadTnt = async () => {
                const createDataSetType = async () =>
                  await createAsyncDataSetTypeFromUploadProd({
                    tntDataSetDescription: fileDescription,
                    tntDataSetName: fileName,
                    tntRecordsFileLocation: {
                      filePath: res.s3Key,
                      fileBucket: res.s3Bucket,
                    },
                    fileTntSource: typeConfig.fileTntSource,
                    usage: uploadType,
                    applyUpload: true,
                  });

                const createDataSetTypeCarrierDefault = async () =>
                  await createAsyncDataSetTypeFromUploadProd({
                    tntDataSetDescription: fileDescription,
                    tntDataSetName: fileName,
                    tntRecordsFileLocation: {
                      filePath: res.s3Key,
                      fileBucket: res.s3Bucket,
                    },
                    fileTntSource: 'CARRIER_DEFAULT',
                    usage: uploadType,
                    applyUpload: true,
                    scope: {
                      carrierId: carrierId || '',
                    },
                  });

                if (type === 'simulation') {
                  return await createDataSetType();
                } else if (type === 'production') {
                  if (!existingDataSetId) return await createDataSetType();
                  else {
                    return await updateDataSetType({
                      tntDataSetId: existingDataSetId,
                      tntDataSetName: fileName,
                      tntDataSetDescription: fileDescription,
                      tntRecordsFileLocation: {
                        filePath: res.s3Key,
                        fileBucket: res.s3Bucket,
                      },
                      applyUpload: true,
                    });
                  }
                } else if (type === 'promise') {
                  if (!existingDataSetId) return await createDataSetType();
                  return await updateDataSetType({
                    tntDataSetId: existingDataSetId,
                    tntDataSetName: fileName,
                    tntDataSetDescription: fileDescription,
                    tntRecordsFileLocation: {
                      filePath: res.s3Key,
                      fileBucket: res.s3Bucket,
                    },
                    applyUpload: true,
                  });
                } else if (type === 'carrier_default') {
                  if (!carrierDefaultDataSetId) return await createDataSetTypeCarrierDefault();
                  else {
                    return await updateDataSetType({
                      tntDataSetId: carrierDefaultDataSetId,
                      tntDataSetName: fileName,
                      tntDataSetDescription: fileDescription,
                      tntRecordsFileLocation: {
                        filePath: res.s3Key,
                        fileBucket: res.s3Bucket,
                      },
                      applyUpload: true,
                    });
                  }
                }
              };
              const tntUploadRes = await uploadTnt();
              const tntUploadError = tntUploadRes as unknown as {
                response?: { status?: number };
                uploadJobStatus?: string;
              };
              // The thunks catch and RETURN the axios error rather than rethrowing, so a failed
              // upload arrives here as a value — the outer catch below never sees it.
              const responseStatus = tntUploadError?.response?.status;
              const isFailure =
                (typeof responseStatus === 'number' && responseStatus >= 400) ||
                (tntUploadError?.uploadJobStatus !== 'IN_PROGRESS' && parseStatusCode(tntUploadError) !== 200);

              if (isFailure) {
                reportUploadFailure(renderUploadError(tntUploadError));
              } else {
                // The job is still parsing at this point. Wait for its terminal status before
                // claiming success — a file can be accepted here and fail validation wholesale.
                setProcessing(true);
                let result;
                try {
                  result = await pollUploadJob({
                    jobId: (tntUploadRes as UploadProdTntResponse).uploadJobId,
                    fetchJob: (jobId) => fetchTntJobById(jobId),
                    sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
                  });
                } finally {
                  setProcessing(false);
                }

                if (result.status === 'FAILED') {
                  reportUploadFailure(renderJobError(result.job));
                }

                // TntManagementContainer only refetches jobs when the dataset id CHANGES. An
                // update-upload reuses the same dataset, so without this the completed job and its
                // new version never reach the table.
                //
                // Isolated from the outcome below: the upload's result is already decided, and a
                // failed refresh must not turn a successful upload into a reported failure.
                if (shouldRefetchJobsAfter(result.status)) {
                  const dataSetId = result.job?.tntDataSetId ?? existingDataSetId ?? undefined;
                  const target = jobRefetchThunkFor(type);
                  try {
                    if (target === 'promise') await fetchPromiseTntJobs(dataSetId);
                    else if (target === 'simulation') await fetchSimulationTntJobs();
                    else if (target === 'carrierDefault') await fetchCarrierDefaultTntJobs(undefined);
                    else await fetchProductionTntJobs(dataSetId);
                  } catch {
                    // The table stays stale until the next fetch; the upload itself is unaffected.
                  }
                }

                if (shouldResetFormAfter(result.status)) {
                  resetForm();
                }

                if (result.status === 'TIMED_OUT') {
                  modal.info({
                    title: 'Upload still processing',
                    content: (
                      <p>
                        The file was accepted and is still being processed. Check the table below for the final result.
                      </p>
                    ),
                  });
                } else if (result.status === 'COMPLETE') {
                  modal.success({
                    title: 'Upload successful',
                  });
                }
              }
            } catch (err) {
              reportUploadFailure(err.message);
            }
          }}
          onFailure={(err) => {
            reportUploadFailure(err.message);
          }}
        >
          <p className="ant-upload-drag-icon">
            <CloudUploadOutlined />
          </p>
          <p className="ant-upload-text">Upload TNT Overrides for {tntUploadTypes[uploadType]}</p>
          <p>Choose files or drag and drop</p>
        </PresignedUpload>
      </Card>
      {contextHolder}
    </Spin>
  );
};
